import Close from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';

import { AUTO_TYPES, STEPS, stepTitles } from './constants';
import DepartureAgentStep from './Steps/DepartureAgentStep';
import DepartureLocalityStep from './Steps/DepartureLocalityStep';
import DepartureModeStep from './Steps/DepartureModeStep';
import DepartureQuantityStep from './Steps/DepartureQuantityStep';
import DepartureTypeStep from './Steps/DepartureTypeStep';

const EMPTY_AGENTS = [];

const DepartureTakeFlow = ({
  open,
  onClose,
  product,
  agents = EMPTY_AGENTS,
  actionLoading = false,
  getAllLocality,
  onAutoInit,
  onInitTask,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = operationSettings?.defaultZone?.key;

  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState(STEPS.MODE);
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedLocality, setSelectedLocality] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      quantity: '',
    },
  });

  useEffect(() => {
    if (!open) {
      setStep(STEPS.MODE);
      setLoading(false);
      setInventoryData(null);
      setSelectedAgent(null);
      setSelectedLocality(null);
      reset({ quantity: '' });

      return;
    }

    reset({
      quantity: String(product?.quantity ?? ''),
    });
  }, [open, product, reset]);

  const validateQuantity = value => {
    const quantity = Number(value);

    if (!value) {
      return 'تعداد برداشت الزامی است.';
    }

    if (!Number.isInteger(quantity) || quantity <= 0) {
      return 'تعداد برداشت باید یک عدد صحیح بیشتر از صفر باشد.';
    }

    const availableQuantity =
      step === STEPS.MANUAL_QTY
        ? selectedLocality?.packQuantity
        : product?.quantity;

    if (availableQuantity != null && quantity > Number(availableQuantity)) {
      return 'مقدار وارد شده نمی‌تواند از موجودی بیشتر باشد.';
    }

    return true;
  };

  const localityGroups = useMemo(() => {
    return Array.isArray(inventoryData?.items) ? inventoryData.items : [];
  }, [inventoryData]);

  const handleManual = async () => {
    if (!product?.productPackGuid) {
      enqueueSnackbar('شناسه بسته‌بندی محصول نامعتبر است.', {
        variant: 'error',
      });

      return;
    }

    setLoading(true);

    const result = await getAllLocality(zoneGuid, product.productPackGuid);

    setLoading(false);

    if (!result) return;

    setInventoryData(result);
    setStep(STEPS.MANUAL_LOCALITY);
  };

  const handleSelectLocality = locality => {
    setSelectedLocality(locality);

    reset({
      quantity: String(locality?.packQuantity ?? locality?.quantity ?? ''),
    });

    setStep(STEPS.MANUAL_QTY);
  };

  const handleConfirmAutoQty = () => {
    if (!agents.length) {
      enqueueSnackbar('ابتدا حداقل یک انباردار به عملیات اضافه کنید.', {
        variant: 'warning',
      });

      return;
    }

    setStep(STEPS.AUTO_AGENT);
  };

  const handleConfirmManualQty = () => {
    setStep(STEPS.MANUAL_AGENT);
  };

  const handleSelectAutoAgent = agent => {
    setSelectedAgent(agent);
    setStep(STEPS.AUTO_TYPE);
  };

  const handleAutoInit = async type => {
    const quantity = getValues('quantity');

    if (!selectedAgent?.guid || !product?.operationProductGuid) {
      return;
    }

    const res = await onAutoInit({
      operationProductGuid: product.operationProductGuid,
      agentGuid: selectedAgent.guid,
      count: quantity,
      type,
    });

    if (res?.status === 200) {
      onClose();
    }
  };

  const handleManualInit = async agent => {
    const quantity = getValues('quantity');

    if (!selectedLocality?.guid || !product?.operationProductGuid) {
      return;
    }

    const ok = await onInitTask({
      operationProductGuid: product.operationProductGuid,
      addressGuid: selectedLocality.guid,
      agentGuid: agent.guid,
      count: quantity,
    });

    if (ok) {
      onClose();
    }
  };

  const busy = loading || actionLoading;

  const renderContent = () => {
    switch (step) {
      case STEPS.MODE:
        return (
          <DepartureModeStep
            busy={busy}
            onManual={handleManual}
            onAuto={() => setStep(STEPS.AUTO_QTY)}
          />
        );

      case STEPS.AUTO_QTY:
        return (
          <DepartureQuantityStep
            busy={busy}
            register={register}
            handleSubmit={handleSubmit}
            errors={errors}
            isSubmitted={isSubmitted}
            validateQuantity={validateQuantity}
            availableQuantity={product?.quantity}
            onSubmit={handleConfirmAutoQty}
          />
        );

      case STEPS.AUTO_AGENT:
        return (
          <DepartureAgentStep
            agents={agents}
            busy={busy}
            onSelect={handleSelectAutoAgent}
          />
        );

      case STEPS.AUTO_TYPE:
        return (
          <DepartureTypeStep
            types={AUTO_TYPES}
            busy={busy}
            onSelect={handleAutoInit}
          />
        );

      case STEPS.MANUAL_LOCALITY:
        return (
          <DepartureLocalityStep
            busy={busy}
            inventoryData={inventoryData}
            localityGroups={localityGroups}
            product={product}
            onSelect={handleSelectLocality}
          />
        );

      case STEPS.MANUAL_QTY:
        return (
          <DepartureQuantityStep
            busy={busy}
            register={register}
            handleSubmit={handleSubmit}
            errors={errors}
            isSubmitted={isSubmitted}
            validateQuantity={validateQuantity}
            availableQuantity={selectedLocality?.packQuantity}
            onSubmit={handleConfirmManualQty}
          />
        );

      case STEPS.MANUAL_AGENT:
        return (
          <DepartureAgentStep
            agents={agents}
            busy={busy}
            onSelect={handleManualInit}
          />
        );

      default:
        return null;
    }
  };

  const handleBack = () => {
    switch (step) {
      case STEPS.AUTO_QTY:
      case STEPS.MANUAL_LOCALITY:
        setStep(STEPS.MODE);
        break;

      case STEPS.AUTO_AGENT:
        setStep(STEPS.AUTO_QTY);
        break;

      case STEPS.AUTO_TYPE:
        setStep(STEPS.AUTO_AGENT);
        break;

      case STEPS.MANUAL_QTY:
        setStep(STEPS.MANUAL_LOCALITY);
        break;

      case STEPS.MANUAL_AGENT:
        setStep(STEPS.MANUAL_QTY);
        break;

      default:
        break;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth={step === STEPS.MANUAL_LOCALITY ? 'md' : 'xs'}
      sx={{ direction: 'rtl' }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {stepTitles[step]}
        </Typography>

        <IconButton size="small" onClick={onClose} disabled={busy}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>{renderContent()}</DialogContent>

      {step !== STEPS.MODE && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" disabled={busy} onClick={handleBack}>
            بازگشت
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DepartureTakeFlow;
